import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { CheckCircleOutline as SuccessIcon } from '@mui/icons-material';

const PublicLeadForm = () => {
  const { companyKey } = useParams();
  const location = useLocation();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const pageUrl = queryParams.get('pageUrl') || '';
  const referrer = queryParams.get('referrer') || '';

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    propertyInterest: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      return 'Name is required.';
    }
    if (!formData.phone.trim()) {
      return 'Phone number is required.';
    }

    // Strip non-digits from phone number and check for exactly 10 digits
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      return 'Phone number must be exactly 10 digits.';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${apiBase}/api/public-leads/submit/${companyKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone.replace(/\D/g, ''),
          email: formData.email || undefined,
          propertyInterest: formData.propertyInterest || undefined,
          message: formData.message || undefined,
          pageUrl: document.referrer || window.location.href,
          referrer: document.referrer || undefined,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || `Failed to submit lead details (HTTP ${response.status})`);
      }

      setSuccess(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        propertyInterest: '',
        message: '',
      });
    } catch (err) {
      console.error('[PublicLeadForm Error] Submission failed:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f8fafc',
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 360,
            textAlign: 'center',
            borderRadius: 4,
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
          }}
        >
          <SuccessIcon sx={{ color: '#10b981', fontSize: 60, mb: 2 }} />
          <Typography variant="h5" fontWeight={800} color="#1e293b" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your inquiry has been submitted successfully. A representative will get in touch with you shortly.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setSuccess(false)}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 700,
              color: '#3b82f6',
              borderColor: '#bfdbfe',
              '&:hover': { borderColor: '#3b82f6', bgcolor: '#eff6ff' },
            }}
          >
            Submit Another Inquiry
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        p: 1.5,
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 360,
          borderRadius: 4,
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.015)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={800} color="#1e293b" gutterBottom>
              Enquire Now
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Please fill out the form below to register your interest.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5, fontSize: '0.825rem' }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              size="small"
              label="Full Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              InputProps={{ style: { borderRadius: 8 } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              size="small"
              label="Phone Number"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              InputProps={{ style: { borderRadius: 8 } }}
            />
            <TextField
              margin="normal"
              fullWidth
              size="small"
              label="Email Address (Optional)"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              InputProps={{ style: { borderRadius: 8 } }}
            />
            <TextField
              margin="normal"
              fullWidth
              size="small"
              label="Property Interest (Optional)"
              value={formData.propertyInterest}
              onChange={(e) => handleInputChange('propertyInterest', e.target.value)}
              InputProps={{ style: { borderRadius: 8 } }}
            />
            <TextField
              margin="normal"
              fullWidth
              size="small"
              label="Message / Requirements"
              multiline
              rows={3}
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              InputProps={{ style: { borderRadius: 8 } }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.25,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: '#3b82f6',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#2563eb', boxShadow: 'none' },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit Inquiry'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PublicLeadForm;
